import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import emailjs from '@emailjs/browser';

// Generate particles once outside component to prevent re-rendering
const generateParticles = () => {
  return Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    delay: Math.random() * 5,
    duration: 8 + Math.random() * 5,
  }));
};

const ComingSoonPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // EmailJS credentials
  const EMAILJS_SERVICE_ID = 'service_4y64zjq';
  const EMAILJS_ADMIN_TEMPLATE_ID = 'template_w4je1pu'; // Template for admin notification
  const EMAILJS_USER_TEMPLATE_ID = 'template_koonfcm'; // Template for user confirmation
  const EMAILJS_PUBLIC_KEY = '7kPBgXtP2Ub1Ppcdn';

  // Memoize particles so they don't regenerate on every render
  const particles = useMemo(() => generateParticles(), []);

  const agentsData = [
    { id: 'sam', emoji: '🧠' },
    { id: 'dana', emoji: '📊' },
    { id: 'ella', emoji: '✉️' },
    { id: 'sean', emoji: '💰' },
    { id: 'ray', emoji: '🧲' },
    { id: 'sara', emoji: '📱' },
    { id: 'chris', emoji: '✍️' },
    { id: 'gio', emoji: '🌍' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const templateParams = {
        name: formData.name,
        email: formData.email,
        company: formData.company || 'Not provided',
        timestamp: new Date().toLocaleString(),
      };

      // Send notification email to admin (you/julien)
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_ADMIN_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      // Send confirmation email to user
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_USER_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      // Store in localStorage as backup
      const submissions = JSON.parse(localStorage.getItem('preRegistrations') || '[]');
      submissions.push({
        ...formData,
        timestamp: new Date().toISOString(),
      });
      localStorage.setItem('preRegistrations', JSON.stringify(submissions));

      setIsSubmitted(true);
    } catch (err) {
      console.error('Failed to send email:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="coming-soon-page">
      {/* Navigation */}
      <nav className="nav-header">
        <Link to="/" className="nav-logo">
          Naabu<span>.</span>
        </Link>
        <Link to="/" className="back-button">
          <ArrowLeft size={20} />
          <span>Back</span>
        </Link>
      </nav>

      {/* Grid Pattern */}
      <div className="grid-pattern" />

      {/* Floating Particles */}
      <div className="particles">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
            }}
          />
        ))}
      </div>

      <div className="coming-soon-container">
        {/* Compact Visual */}
        <div className="compact-visual">
          <div className="agent-icons">
            {agentsData.slice(0, 6).map((agent, i) => (
              <div
                key={agent.id}
                className="agent-icon"
                style={{
                  animationDelay: `${i * 0.2}s`,
                }}
              >
                {agent.emoji}
              </div>
            ))}
          </div>
        </div>

        {/* Coming Soon Content */}
        <div className="coming-soon-content">
          <h1 className="coming-soon-title">Something exciting is coming</h1>

          <p className="coming-soon-subtitle">
            Naabu is your AI Manager that automatically assigns the right specialists to get real work done.
          </p>

          {!isSubmitted ? (
            <>
              <p className="coming-soon-description">
                Be the first to know when we launch.
              </p>

              <form onSubmit={handleSubmit} className="pre-registration-form">
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="form-input"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="form-input"
                />
                <input
                  type="text"
                  name="company"
                  placeholder="Company (optional)"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="form-input"
                />
                {error && <p className="error-message">{error}</p>}
                <button type="submit" className="submit-button" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Pre-register now'}
                </button>
              </form>
            </>
          ) : (
            <div className="success-message">
              <h2>Thank you for registering!</h2>
              <p>We'll reach out to you when we launch.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComingSoonPage;
